import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  MessageCircle, X, Send, Loader2, ChevronDown, User, Bot, 
  AlertTriangle, ArrowRight, Minimize2, Maximize2, Headphones
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_REPLIES_DEFAULT = [
  "What plans do you offer?",
  "How do I reset my password?",
  "How does scholarship matching work?",
  "I need help with billing",
];

export default function SupportBot() {
  const { user, getAuthHeader } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `support_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const [showEscalation, setShowEscalation] = useState(false);
  const [escalationForm, setEscalationForm] = useState({ name: '', email: '', description: '' });
  const [suggestedActions, setSuggestedActions] = useState(QUICK_REPLIES_DEFAULT);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = user 
        ? `Hi ${user.full_name?.split(' ')[0] || 'there'}! I'm the Agentic FuturePath AI Support Assistant. How can I help you today?`
        : "Hi there! I'm the Agentic FuturePath AI Support Assistant. I can help with billing, account issues, platform features, and more. How can I help?";
      setMessages([{ role: 'bot', content: greeting, timestamp: new Date().toISOString() }]);
      if (user) {
        setEscalationForm(prev => ({ ...prev, name: user.full_name || '', email: user.email || '' }));
      }
    }
  }, [isOpen, user]);

  const sendMessage = useCallback(async (text) => {
    const messageText = text || input.trim();
    if (!messageText || loading) return;
    
    setInput('');
    setSuggestedActions([]);
    setMessages(prev => [...prev, { role: 'user', content: messageText, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const headers = user ? getAuthHeader()?.headers || {} : {};
      const response = await axios.post(`${API}/support/chat`, {
        message: messageText,
        session_id: sessionId,
        user_context: !user ? { page: window.location.pathname } : undefined
      }, { headers });

      const data = response.data;
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: data.response,
        confidence: data.confidence,
        category: data.category,
        timestamp: new Date().toISOString()
      }]);
      
      if (data.suggested_actions?.length > 0) {
        setSuggestedActions(data.suggested_actions);
      }
      
      if (data.should_escalate) {
        setShowEscalation(true);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: "I'm having trouble connecting right now. Please try again or click 'Talk to Human' below.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, user, getAuthHeader, sessionId]);

  const handleEscalate = async () => {
    if (!escalationForm.email) {
      toast.error('Please enter your email address');
      return;
    }
    
    const conversationSummary = messages.map(m => `${m.role === 'user' ? 'Customer' : 'Bot'}: ${m.content}`).join('\n');
    
    try {
      await axios.post(`${API}/support/tickets`, {
        name: escalationForm.name || 'Anonymous',
        email: escalationForm.email,
        subject: `Support Escalation - ${new Date().toLocaleDateString()}`,
        description: escalationForm.description || 'Customer requested human support',
        category: messages.find(m => m.category)?.category || 'general',
        session_id: sessionId,
        conversation_summary: conversationSummary.slice(0, 2000),
      });
      
      setShowEscalation(false);
      setMessages(prev => [...prev, {
        role: 'bot',
        content: "I've created a support ticket and our team will reach out to you via email. We typically respond within 24 hours. Is there anything else I can help with in the meantime?",
        timestamp: new Date().toISOString()
      }]);
      setSuggestedActions(["No, that's all", "I have another question"]);
      toast.success('Support ticket created! Check your email.');
    } catch (error) {
      toast.error('Failed to create ticket. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        data-testid="support-bot-trigger"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-lg shadow-emerald-500/30 flex items-center justify-center hover:scale-110 transition-transform group"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Need help?
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed z-50 ${isExpanded ? 'inset-4 md:inset-8' : 'bottom-6 left-6 w-[380px] h-[560px]'} transition-all duration-300`}
      data-testid="support-bot-window"
    >
      <div className="flex flex-col h-full bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Support Assistant</h3>
              <span className="text-emerald-100 text-xs flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-300 rounded-full" /> Online
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setIsExpanded(!isExpanded)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
              {isExpanded ? <Minimize2 className="w-4 h-4 text-white" /> : <Maximize2 className="w-4 h-4 text-white" />}
            </button>
            <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" data-testid="support-bot-close">
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'bot' && (
                <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot className="w-4 h-4 text-emerald-400" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                msg.role === 'user' 
                  ? 'bg-blue-600 text-white rounded-br-md' 
                  : 'bg-slate-800 text-slate-200 rounded-bl-md'
              }`}>
                {msg.content.split('\n').map((line, j) => (
                  <p key={j} className={j > 0 ? 'mt-1.5' : ''}>{line}</p>
                ))}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <User className="w-4 h-4 text-blue-400" />
                </div>
              )}
            </div>
          ))}
          
          {loading && (
            <div className="flex gap-2 items-start">
              <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="bg-slate-800 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          {/* Escalation Form */}
          {showEscalation && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                <AlertTriangle className="w-4 h-4" />
                Connect with Human Support
              </div>
              <input
                type="text"
                placeholder="Your name"
                value={escalationForm.name}
                onChange={e => setEscalationForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder:text-slate-300"
                data-testid="escalation-name"
              />
              <input
                type="email"
                placeholder="Your email *"
                value={escalationForm.email}
                onChange={e => setEscalationForm(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder:text-slate-300"
                data-testid="escalation-email"
              />
              <textarea
                placeholder="Describe your issue (optional)"
                value={escalationForm.description}
                onChange={e => setEscalationForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder:text-slate-300 resize-none"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleEscalate} className="bg-amber-500 hover:bg-amber-600 text-black font-medium flex-1" data-testid="submit-ticket-btn">
                  Submit Ticket
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowEscalation(false)} className="text-slate-300">
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {suggestedActions.length > 0 && !loading && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {suggestedActions.map((action, i) => (
              <button
                key={i}
                onClick={() => sendMessage(action)}
                className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full border border-slate-700/50 transition-colors"
                data-testid={`quick-reply-${i}`}
              >
                {action}
              </button>
            ))}
          </div>
        )}

        {/* Talk to Human (always visible) */}
        {!showEscalation && (
          <div className="px-4 pb-2">
            <button
              onClick={() => setShowEscalation(true)}
              className="w-full text-xs text-slate-300 hover:text-amber-400 transition-colors flex items-center justify-center gap-1 py-1"
              data-testid="talk-to-human-btn"
            >
              <Headphones className="w-3 h-3" /> Talk to Human
            </button>
          </div>
        )}

        {/* Input */}
        <div className="p-3 border-t border-slate-700/50 flex-shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your question..."
              className="flex-1 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder:text-slate-300 focus:outline-none focus:border-emerald-500"
              data-testid="support-bot-input"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              size="icon"
              className="bg-emerald-600 hover:bg-emerald-500 rounded-xl h-10 w-10"
              data-testid="support-bot-send"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
